import React, { Fragment, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "src/components/UI/Button/Button";
import { ModalElementContext } from "src/context/modal-element-context";
import { Calendar } from "src/components/features/search/SearchForm/elements/Calendar/Calendar";
import SearchFormDate from "src/components/features/search/SearchForm/elements/SearchFormDate/SearchFormDate";
import { TRIP_TYPE } from "src/constants";
import { searchDataActions, selectTripType } from "src/store/search";
import { calendarApi } from "src/services/api";
import dayjs from "dayjs";
import classes from "./DateSelection.module.css";
import { isSameDate, secondDateIsLater } from "src/utils/date-utils";
import { CloseWindowButton } from "src/components/UI/CloseWindowButton/CloseWindowButton";
import { useDeviceContext } from "src/context/device-context";
import { WEEKDAY_SHORT_STR_MAP } from "src/constants/string-res-map";
import { useLocaleContext } from "src/context/locale-context";

function DateSelection({ isDeparture }) {
  const dispatch = useDispatch();
  const { onClose, targetTripId, isResultsModal, dispatchLocalState, resultsSFData } =
    useContext(ModalElementContext);
  const { isMobile } = useDeviceContext();

  const tripType = useSelector((state) => {
    if (isResultsModal) {
      return resultsSFData.tripType;
    } else {
      return selectTripType(state);
    }
  });
  const allTrips = useSelector((state) => {
    if (isResultsModal) {
      return resultsSFData.trips;
    } else {
      return state.search.trips;
    }
  });
  const tripNum = allTrips.findIndex((trip) => trip.id === targetTripId);

  const trip = allTrips[tripNum];
  const isRoundTrip = tripType === TRIP_TYPE.roundtrip;
  const isMultiTrip = tripType === TRIP_TYPE.multicity;

  const [startDate, setStartDate] = useState(trip.startDate);
  const [endDate, setEndDate] = useState(trip.endDate);
  const [fieldFocus, setFieldFocus] = useState(
    !startDate || !isRoundTrip ? { start: true, end: false } : { start: false, end: true }
  );
  const hasBothDatesOnOpen = !!startDate && !!endDate;
  const [isSelectingEndDate, setIsSelectingEndDate] = useState(
    tripType === TRIP_TYPE.roundtrip &&
      ((hasBothDatesOnOpen && !isDeparture) || (startDate && !endDate))
  );

  const [directFlightDates, setDirectFlightDates] = useState([]);

  const originIata = trip?.srcLocation?.iata;
  const destinationIata = trip?.destLocation?.iata;

  useEffect(() => {
    if (!originIata || !trip?.destLocation) {
      setDirectFlightDates([]);
      return;
    }

  const origins = trip.srcLocation?.airports?.length > 0
    ? trip.srcLocation.airports.join(",")
    : trip.srcLocation?.iata;

  const destinations = trip.destLocation.airports?.length > 0
    ? trip.destLocation.airports.join(",")
    : trip.destLocation.iata;

  if (!origins || !destinations) {
    setDirectFlightDates([]);
    return;
  }

calendarApi
  .getDirectFlightDates(origins, destinations)
      .then((res) => {
        const today = dayjs().format("YYYY-MM-DD");
        const filtered = (res.data || []).filter((date) => date >= today);
        setDirectFlightDates(filtered);
      })
      .catch(() => setDirectFlightDates([]));
  }, [originIata, destinationIata]);

  const onStartDateSelectedHandler = (date) => {
    setStartDate(date);
    if (
      isRoundTrip &&
      endDate &&
      !(isSameDate(date, endDate) || secondDateIsLater(date, endDate))
    ) {
      setEndDate("");
    }
    if (isRoundTrip) {
      focusEndField();
    }
  };

  const onEndDateSelectedHandler = (date) => {
    if (startDate && secondDateIsLater(date, startDate)) {
      onStartDateSelectedHandler(date);
    } else {
      setEndDate(date);
      focusStartField();
    }
  };

  const onDesktopStartDateSelectedHandler = (date) => {
    setStartDate(date);
    const payload = { tripId: targetTripId, date };
    dispatch(searchDataActions.updateStartDate(payload));
    if (
      isRoundTrip &&
      endDate &&
      !(isSameDate(date, endDate) || secondDateIsLater(date, endDate))
    ) {
      dispatch(searchDataActions.updateEndDate({ ...payload, date: "" }));
    }
    if (isMultiTrip) {
      dispatch(searchDataActions.updateMultiTripStartDates({ tripId: targetTripId }));
    }

    if (hasBothDatesOnOpen || isRoundTrip) {
      setEndDate("");
      focusEndField();
    } else {
      onClose();
    }
  };

  const onDesktopEndDateSelectedHandler = (date) => {
    if (startDate && secondDateIsLater(date, startDate)) {
      onDesktopStartDateSelectedHandler(date);
    } else {
      setEndDate(date);
      const payload = { tripId: targetTripId, date: startDate };
      dispatch(searchDataActions.updateStartDate(payload));
      dispatch(searchDataActions.updateEndDate({ ...payload, date }));
      onClose();
    }
  };

  const onCloseWithoutSaving = () => {
    onClose();
  };

  const onConfirmDates = () => {
    if (isResultsModal) {
      const dispatchObj = { type: "UPDATE_TRIP", id: targetTripId };
      dispatchLocalState({ ...dispatchObj, payload: { startDate: startDate } });
      if (isRoundTrip) {
        dispatchLocalState({ ...dispatchObj, payload: { endDate: endDate } });
      } else if (isMultiTrip) {
        dispatchLocalState({ type: "UPDATE_MULTI_START_DATES", id: targetTripId });
      }
    } else {
      const payload = { tripId: targetTripId, date: startDate };
      dispatch(searchDataActions.updateStartDate({ ...payload }));
      if (isRoundTrip) {
        payload.date = endDate;
        dispatch(searchDataActions.updateEndDate({ ...payload }));
      } else if (isMultiTrip) {
        dispatch(searchDataActions.updateMultiTripStartDates({ tripId: targetTripId }));
      }
    }
    onClose();
  };

  const onClearInput = (type = "end") => {
    if (type === "end") {
      setEndDate("");
      focusEndField();
    } else if (type === "start") {
      setStartDate("");
      focusStartField();
    }
  };

  const focusStartField = () => {
    setFieldFocus({ start: true, end: false });
    setIsSelectingEndDate(false);
  };

  const focusEndField = () => {
    setFieldFocus({ start: false, end: true });
    setIsSelectingEndDate(true);
  };

  useEffect(() => {
    if (!isMobile && !trip.startDate && startDate) {
      setStartDate("");
      focusStartField();
    }
  }, [trip.startDate, startDate, isMobile]);

  const latestMinDate =
    tripNum > 0
      ? allTrips
          .slice(0, tripNum)
          .map((t) => t.startDate)
          .reduce((latest, date) => (secondDateIsLater(latest, date) ? date : latest))
      : "";

  const props = {
    minDate: isMultiTrip && tripNum > 0 ? latestMinDate : "",
    onCloseWithoutSaving: onCloseWithoutSaving,
    tripType: tripType,
    startDate: startDate,
    endDate: endDate,
    isSelectingEndDate: isSelectingEndDate,
    onClearInput: onClearInput,
    startDateClass: fieldFocus.start ? classes.selected : "",
    endDateClass: fieldFocus.end ? classes.selected : "",
    directFlightDates,
  };

  if (isMobile) {
    props.onStartDateSelectedHandler = onStartDateSelectedHandler;
    props.onEndDateSelectedHandler = onEndDateSelectedHandler;
    props.onConfirmDates = onConfirmDates;
    props.onFocusStartDate = focusStartField;
    props.onFocusEndDate = focusEndField;
  } else {
    props.onStartDateSelectedHandler = onDesktopStartDateSelectedHandler;
    props.onEndDateSelectedHandler = onDesktopEndDateSelectedHandler;
    props.isSpecificDateType = hasBothDatesOnOpen;
  }

  return (
    <Fragment>
      {isMobile && <MobileCalendar {...props} />}
      {!isMobile && <DesktopCalendar {...props} />}
    </Fragment>
  );
}

function DesktopCalendar({
  tripType,
  minDate,
  startDate,
  endDate,
  onEndDateSelectedHandler,
  onStartDateSelectedHandler,
  isSelectingEndDate,
  isSpecificDateType,
  directFlightDates,
}) {
  const { getStringRes } = useLocaleContext(); 
  return (
    <Fragment>
      <Calendar
        tripType={tripType}
        minDate={minDate}
        startDate={startDate}
        endDate={endDate}
        onEndDateSelected={onEndDateSelectedHandler}
        onStartDateSelected={onStartDateSelectedHandler}
        isSelectingEndDate={isSelectingEndDate}
        isSpecificDateType={isSpecificDateType}
        directFlightDates={directFlightDates}
      />
      <div className={`${classes.legend} ${classes.legendDesktop}`}>
        <div className={classes.legendItem}>
          <span className={classes.legendDot}></span>
          <span>{getStringRes("sf.calendar.legend.direct")}</span>
        </div>
        <p className={classes.legendSubtitle}>
          {getStringRes("sf.calendar.legend.subtitle")}
        </p>
      </div>
    </Fragment>
  );
}

function MobileCalendar({
  onCloseWithoutSaving,
  tripType,
  minDate,
  startDate,
  endDate,
  onEndDateSelectedHandler,
  onStartDateSelectedHandler,
  isSelectingEndDate,
  onClearInput,
  startDateClass,
  endDateClass,
  onConfirmDates,
  onFocusStartDate,
  onFocusEndDate,
  directFlightDates,  
}) {
  const { getStringRes } = useLocaleContext();
  const weekdays = WEEKDAY_SHORT_STR_MAP.map((wkd) => getStringRes(wkd));
  const isRoundTrip = tripType === TRIP_TYPE.roundtrip;

  return (
    <Fragment>
      <CloseWindowButton onClick={onCloseWithoutSaving} />
      <div className={classes.weekdays}>
        {weekdays.map((wd) => (
          <span key={wd}>{wd}</span>
        ))}
      </div>
      <Calendar
        tripType={tripType}
        minDate={minDate}
        startDate={startDate}
        endDate={endDate}
        onEndDateSelected={onEndDateSelectedHandler}
        onStartDateSelected={onStartDateSelectedHandler}
        isSelectingEndDate={isSelectingEndDate}
        isScroll
        directFlightDates={directFlightDates}
      />
      <div className={classes.legend}>
        <div className={classes.legendItem}>
          <span className={classes.legendDot}></span>
          <span>{getStringRes("sf.calendar.legend.direct")}</span>
        </div>
        <p className={classes.legendSubtitle}>
          {getStringRes("sf.calendar.legend.subtitle")}
        </p>
      </div>
      <div className={classes.drawer}>
        <div className={classes.datepicker}>
          <SearchFormDate
            id="calendar-start-date"
            className={`${classes.startDate} ${startDateClass}`}
            value={startDate}
            label={getStringRes("sf.field.date.depart")}
            onClear={startDate ? () => onClearInput("start") : null}
            onClick={isRoundTrip ? onFocusStartDate : null}
          />
          {isRoundTrip && (
            <SearchFormDate
              id="calendar-end-date"
              className={`${classes.endDate} ${endDateClass} ${
                isSelectingEndDate && !endDate ? classes.small : ""
              }`}
              value={endDate}
              label={
                isSelectingEndDate && !endDate
                  ? getStringRes("sf.field.date.return.select")
                  : getStringRes("sf.field.date.return")
              }
              onClear={() => onClearInput("end")}
              onClick={onFocusEndDate}
            />
          )}
        </div>
        <Button
          name={getStringRes("sf.mob.datepicker.select")}
          onClick={onConfirmDates}
          opts={{ height: "3.5rem" }}
          fontclass={classes.fontclass}
          cornerSm
          wide
        />
      </div>
    </Fragment>
  );
}

export default DateSelection;
